import { useState, useContext, useEffect, useMemo, useCallback } from 'react'
import { TextField, Typography, Button, FormControlLabel, FormLabel, RadioGroup, Radio, Switch, Stack, Checkbox } from '@mui/material'
import { validate as validateEmail } from "react-email-validator"
import _ from 'lodash'

import { AppContext } from '/providers/app-context'
import { checkEmailAvailability, requestEmailVerificationCode, verifyEmailCode } from '/providers/auth-api'
import { onboarding } from '/providers/studio-api'

import PageWrapper from '/components/page-wrapper'
import HelpText from '/help/error'

const isValidPhoneNumber = value => {
  if (!value) return false
  const digits = value.replace(/\D/g, '')
  return digits.length >= 7 && digits.length <= 15
}

const isRelayEmail = email => typeof email === 'string' && email.toLowerCase().includes('@privaterelay.appleid.com')

const Onboarding = () => {

  const { apiSource, apiKeys, user, client, setUser, setClient } = useContext(AppContext)
  const [clientType, setClientType] = useState("company")
  const [newUser, setNewUser] = useState()
  const [newClient, setNewClient] = useState()
  const [fieldErrors, setFieldErrors] = useState({})
  const [pendingEmail, setPendingEmail] = useState('')
  const [emailVerified, setEmailVerified] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [checkingEmail, setCheckingEmail] = useState(false)
  const [sendingVerification, setSendingVerification] = useState(false)
  const [verificationRequested, setVerificationRequested] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [verificationError, setVerificationError] = useState('')
  const [verificationInfo, setVerificationInfo] = useState('')
  const [verifyingCode, setVerifyingCode] = useState(false)
  const [terms, setTerms] = useState({
    payg: false,
    dpa: false,
    fairUse: false
  })

  useEffect(() => {
    setNewUser({ ...user })
    if (user?.email) {
      setPendingEmail(user.email)
      setEmailVerified(true)
      setVerificationRequested(false)
      setVerificationCode('')
    } else {
      setPendingEmail('')
      setEmailVerified(false)
      setVerificationRequested(false)
      setVerificationCode('')
    }
  }, [user])

  useEffect(() => {
    setNewClient({ ...client })
  }, [client])

  useEffect(() => {
    if (!newUser) return
    if (newUser.email_verified) {
      setPendingEmail(newUser.email)
      setEmailVerified(true)
      setEmailError('')
      setVerificationError('')
      setVerificationInfo('Email verified')
      setVerificationRequested(false)
      setVerificationCode('')
    }
  }, [newUser?.email])

  const validate = useCallback(() => {
    if (!newUser || !newClient) return {}

    const errors = {}

    if (!newUser.username || newUser.username.trim().length < 3) {
      errors.username = 'Enter a name with at least 3 characters'
    }

    const emailToValidate = newUser.email ?? pendingEmail

    if (!emailToValidate) {
      errors.userEmail = 'Email address is required'
    } else if (!validateEmail(emailToValidate)) {
      errors.userEmail = 'Enter a valid email address'
    } else if (isRelayEmail(emailToValidate)) {
      errors.userEmail = 'Apple private relay email addresses are not supported'
    }

    if (!newUser.email && !emailVerified) {
      errors.userEmailVerification = 'Please verify your email before continuing'
    }

    if (!terms.payg) {
      errors.payg = 'You must accept the Pay-as-you-go Terms'
    }
    if (!terms.dpa) {
      errors.dpa = 'You must accept the Data Processing Agreement'
    }
    if (!terms.fairUse) {
      errors.fairUse = 'You must accept the Fair Use Policy'
    }

    if (clientType === 'company') {
      if (!newClient.name || newClient.name.trim().length < 3) {
        errors.companyName = 'Enter a company name'
      }

      if (!newClient.address || newClient.address.trim().length === 0) {
        errors.address = 'Company address is required'
      }

      if (!newClient.main_contact_name || newClient.main_contact_name.trim().length === 0) {
        errors.mainContactName = 'Main contact name is required'
      }

      if (!newClient.main_contact_email || !validateEmail(newClient.main_contact_email)) {
        errors.mainContactEmail = 'Enter a valid main contact email'
      } else if (isRelayEmail(newClient.main_contact_email)) {
        errors.mainContactEmail = 'Apple private relay email addresses are not supported'
      }

      if (!newClient.main_contact_phone || !isValidPhoneNumber(newClient.main_contact_phone)) {
        errors.mainContactPhone = 'Enter a valid phone number'
      }

      if (!newClient.billing_email || !validateEmail(newClient.billing_email)) {
        errors.billingEmail = 'Enter a valid billing email'
      } else if (isRelayEmail(newClient.billing_email)) {
        errors.billingEmail = 'Apple private relay email addresses are not supported'
      }
    }

    return errors
  }, [clientType, newClient, newUser, pendingEmail, emailVerified])

  const validated = useMemo(() => {
    if (!newUser || !newClient) return false
    const errors = validate()
    return Object.keys(errors).length === 0
  }, [newUser, newClient, validate])

  useEffect(() => {
    if (!Object.keys(fieldErrors).length) return
    const updatedErrors = validate()
    if (!_.isEqual(fieldErrors, updatedErrors)) {
      setFieldErrors(updatedErrors)
    }
  }, [fieldErrors, validate])

  const triggerValidation = () => {
    const errors = validate()
    setFieldErrors(errors)
  }

  const handleEmailChange = e => {
    const value = e.target.value
    setPendingEmail(value)
    setEmailError('')
    setVerificationError('')
    setVerificationInfo('')
    setVerificationRequested(false)
    setVerificationCode('')
    if (emailVerified) {
      setEmailVerified(false)
    }
    if (newUser?.email) {
      updateUser("email", undefined)
    }
  }

  const handleSendVerification = async () => {
    const trimmed = pendingEmail.trim()
    setEmailError('')
    setVerificationError('')
    setVerificationInfo('')

    if (!trimmed) {
      setEmailError('Email address is required')
      return
    }

    if (!validateEmail(trimmed)) {
      setEmailError('Enter a valid email address')
      return
    }

    if (isRelayEmail(trimmed)) {
      setEmailError('Non-relay email addresses are required')
      return
    }

    setCheckingEmail(true)
    const availability = await checkEmailAvailability({ apiSource, apiKeys, email: trimmed })
    setCheckingEmail(false)

    if (availability?.error) {
      setEmailError(typeof availability.error === 'string' ? availability.error : 'Unable to validate email')
      return
    }

    if (availability?.available === false) {
      setEmailError('That email address is already in use')
      return
    }

    setSendingVerification(true)
    const verification = await requestEmailVerificationCode({ apiSource, apiKeys, email: trimmed, user_id: user.user_id, username: newUser.username })
    setSendingVerification(false)

    if (verification?.error) {
      setEmailError(typeof verification.error === 'string' ? verification.error : 'Unable to send verification email')
      return
    }

    setVerificationRequested(true)
    setVerificationInfo('Verification code sent. Please check your inbox.')
    setVerificationCode('')
  }

  const handleVerifyCode = async () => {
    if (!verificationRequested) {
      setVerificationError('Request a verification code first.')
      return
    }

    const code = verificationCode.trim()

    if (!code) {
      setVerificationError('Enter the verification code sent to your email.')
      return
    }

    setVerifyingCode(true)
    const result = await verifyEmailCode({ apiSource, apiKeys, user_id: user.user_id, code })
    setVerifyingCode(false)

    if (result.error) {
      setVerificationError(typeof result.error === 'string' ? result.error : 'Verification failed')
      setEmailVerified(false)
      return
    }

    if (result.expired === true) {
      setVerificationError(result?.message || 'Verification code expired. Please verufy again.')
      setEmailVerified(false)
      return
    }

    if (result.verified === false || result?.ok === false) {
      setVerificationError(result?.message || 'Verification code not accepted')
      setEmailVerified(false)
      return
    }

    setEmailVerified(true)
    setVerificationRequested(false)
    setVerificationCode('')
    setVerificationError('')
    setVerificationInfo('Email verified')
    updateUser("email", pendingEmail.trim())
    triggerValidation()
  }

  const updateUser = (field, value) => {
    setNewUser(prev => {
      let temp = { ...prev }
      _.set(temp, field, value)
      return temp
    })
  }

  const updateClient = (field, value) => {
    setNewClient(prev => {
      let temp = { ...prev }
      _.set(temp, field, value)
      return temp
    })
  }

  const updateTerms = (field, checked) => {
    setTerms(prev => ({ ...prev, [field]: checked }))
  }

  const save = async () => {
    // Save to DB
    const result = await onboarding({ apiSource, apiKeys, user: newUser, client: newClient })

    if (result.ok) {
      setClient(result.client)
      setUser(result.user)
    }
  }

  if (newClient && newUser) return (
    <PageWrapper title="Onboarding" HelpText={HelpText}>
      <div className='flex flex-col max-w-fit p-10 gap-5 overflow-y-scroll h-full w-full'>
        <Typography variant='h1'>Welcome to Smartix</Typography>

        <Typography variant='h4'>Please complete the form below to complete your onboarding</Typography>

        <FormLabel id="data_type_label">Signup type</FormLabel>
        <RadioGroup
          row
          aria-labelledby="data_type_label"
          value={clientType}
          onChange={e => setClientType(e.target.value)}
        >
          <FormControlLabel value="company" control={<Radio />} label="I represent a company" />
          <FormControlLabel value="personal" control={<Radio />} label="I am a sole trader or personal user" />
        </RadioGroup>

        <div className='flex flex-wrap gap-10 w-full justify-between'>
          <div className="flex flex-col gap-5 p-5 w-[600px] border border-white/20 dark:border-white/10 bg-white/5 dark:bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg">
            <Typography variant='h5'>User details</Typography>

            <TextField
              label="Your name"
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
              value={newUser.username ?? ""}
              error={!!fieldErrors.username}
              helperText={fieldErrors.username}
              required={true}
              onChange={e => updateUser("username", e.target.value)} />

            <div className='flex flex-col gap-3'>
              <TextField
                label="Email"
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
                value={pendingEmail}
                error={!!(emailError || fieldErrors.userEmail)}
                helperText={emailError || fieldErrors.userEmail}
                required={true}
                onChange={handleEmailChange}
              />
              <div className='flex flex-row gap-3 items-center'>
                <Button
                  variant='outlined'
                  onClick={handleSendVerification}
                  disabled={checkingEmail || sendingVerification}
                >
                  {checkingEmail ? 'Checking...' : (sendingVerification ? 'Sending...' : 'Verify email')}
                </Button>
              </div>

              {verificationInfo && (
                <Typography sx={{ color: emailVerified ? 'success.main' : 'text.secondary' }}>
                  {verificationInfo}
                </Typography>
              )}
              {verificationRequested && (
                <div className='flex flex-row gap-3 items-center'>
                  <TextField
                    label="Verification code"
                    slotProps={{ inputLabel: { shrink: true } }}
                    value={verificationCode}
                    onChange={e => setVerificationCode(e.target.value)}
                    error={!!verificationError}
                    helperText={verificationError}
                  />
                  <Button
                    variant='contained'
                    onClick={handleVerifyCode}
                    disabled={verifyingCode}
                  >
                    {verifyingCode ? 'Verifying...' : 'Confirm code'}
                  </Button>
                </div>
              )}
              {fieldErrors.userEmailVerification && (
                <Typography sx={{ color: 'error.main' }}>
                  {fieldErrors.userEmailVerification}
                </Typography>
              )}
            </div>

            <div className='flex flex-row justify-between'>
              <Typography>Allow user to administer account?</Typography>
              <div>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography>No</Typography>
                  <Switch checked={newUser.permissions.client_admin} onChange={e => updateUser("permissions.client_admin", e.target.checked)} disabled={true} />
                  <Typography>Yes</Typography>
                </Stack>
              </div>
            </div>

            <div className='flex flex-row justify-between'>
              <Typography>Enable access to Studio?</Typography>
              <div>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography>No</Typography>
                  <Switch checked={newUser.permissions.studio} onChange={e => updateUser("permissions.studio", e.target.checked)} disabled={true} />
                  <Typography>Yes</Typography>
                </Stack>
              </div>
            </div>

            <div className='flex flex-row justify-between'>
              <Typography>Enable access to Smartix Scan?</Typography>
              <div>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography>No</Typography>
                  <Switch checked={newUser.permissions.scanner} onChange={e => updateUser("permissions.scanner", e.target.checked)} />
                  <Typography>Yes</Typography>
                </Stack>
              </div>
            </div>
            <div className='opacity-60 -mt-5 mb-3 italic text-sm'>NOTE: You may prefer to create a separate user account for Scan app.</div>

            <div className='flex flex-row justify-between'>
              <div>Preferred data format</div>
              <div>
                <RadioGroup
                  aria-labelledby="data_type_label"
                  value={newUser.preferences.data_format}
                  onChange={e => updateUser("preferences.data_format", e.target.value)}
                >
                  <FormControlLabel value="json" labelPlacement="start" control={<Radio />} label="JSON" />
                  <FormControlLabel value="csv" labelPlacement="start" control={<Radio />} label="CSV" />
                  <FormControlLabel value="xls" labelPlacement="start" control={<Radio />} label="Microsoft Excel" />
                </RadioGroup>
              </div>
            </div>
          </div>

          {clientType === "company" &&
            <div className="flex flex-col gap-5 p-5 w-[600px] border border-white/20 dark:border-white/10 bg-white/5 dark:bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg">
              <Typography variant='h5'>Company details</Typography>

              <TextField
                label="Company name"
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
                value={newClient.name ?? ""}
                error={!!fieldErrors.companyName}
                helperText={fieldErrors.companyName}
                required={true}
                onChange={e => updateClient("name", e.target.value)} />

              <TextField
                label="Company registration number"
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
                value={newClient.company_number ?? ""}
                onChange={e => updateClient("company_number", e.target.value)} />

              <TextField
                label="VAT number"
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
                value={newClient.vat_number ?? ""}
                onChange={e => updateClient("vat_number", e.target.value)} />

              <TextField
                label="Company address"
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
                multiline
                minRows={3}
                value={newClient.address ?? ""}
                error={!!fieldErrors.address}
                helperText={fieldErrors.address}
                required={true}
                onChange={e => updateClient("address", e.target.value)} />

              <TextField
                label="Main company contact name"
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
                value={newClient.main_contact_name ?? ""}
                error={!!fieldErrors.mainContactName}
                helperText={fieldErrors.mainContactName}
                required={true}
                onChange={e => updateClient("main_contact_name", e.target.value)} />

              <TextField
                label="Main company contact email"
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
                value={newClient.main_contact_email ?? ""}
                error={!!fieldErrors.mainContactEmail}
                helperText={fieldErrors.mainContactEmail}
                required={true}
                onChange={e => updateClient("main_contact_email", e.target.value)} />

              <TextField
                label="Main company contact phone number"
                slotProps={{ inputLabel: { shrink: true } }}
                required={true}
                fullWidth
                value={newClient.main_contact_phone ?? ""}
                error={!!fieldErrors.mainContactPhone}
                helperText={fieldErrors.mainContactPhone}
                onChange={e => updateClient("main_contact_phone", e.target.value)} />

              <TextField
                label="Billing email"
                slotProps={{ inputLabel: { shrink: true } }}
                required={true}
                fullWidth
                value={newClient.billing_email ?? ""}
                error={!!fieldErrors.billingEmail}
                helperText={fieldErrors.billingEmail}
                onChange={e => updateClient("billing_email", e.target.value)} />
            </div>}
        </div>

        <div className='flex flex-col gap-3 w-full max-w-[800px]'>
          <Typography variant='h6'>Agreements</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={terms.payg}
                onChange={e => updateTerms('payg', e.target.checked)}
                color="primary"
              />
            }
            label={
              <span>
                I agree to the{' '}
                <a href="https://www.smartix.uk/terms/payg-terms/" target="_blank" rel="noreferrer">Pay-as-you-go Terms</a>.
              </span>
            }
          />
          {fieldErrors.payg && <Typography sx={{ color: 'error.main' }}>{fieldErrors.payg}</Typography>}

          <FormControlLabel
            control={
              <Checkbox
                checked={terms.dpa}
                onChange={e => updateTerms('dpa', e.target.checked)}
                color="primary"
              />
            }
            label={
              <span>
                I agree to the{' '}
                <a href="https://www.smartix.uk/terms/dpa/" target="_blank" rel="noreferrer">Data Processing Agreement</a>.
              </span>
            }
          />
          {fieldErrors.dpa && <Typography sx={{ color: 'error.main' }}>{fieldErrors.dpa}</Typography>}

          <FormControlLabel
            control={
              <Checkbox
                checked={terms.fairUse}
                onChange={e => updateTerms('fairUse', e.target.checked)}
                color="primary"
              />
            }
            label={
              <span>
                I agree to the{' '}
                <a href="https://www.smartix.uk/terms/fair-use-policy/" target="_blank" rel="noreferrer">Fair Use Policy</a>.
              </span>
            }
          />
          {fieldErrors.fairUse && <Typography sx={{ color: 'error.main' }}>{fieldErrors.fairUse}</Typography>}
        </div>

        <div className='flex flex-row justify-end'>
          <span onMouseEnter={triggerValidation}>
            <Button
              onClick={save}
              variant='contained'
              disabled={!validated}
            >
              Save
            </Button>
          </span>
        </div>
      </div>

    </PageWrapper>
  )
}

export default Onboarding
